import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Station } from '../types';
import { GOOGLE_MAPS_API_KEY, CUSTOM_MAP_STYLE } from '../constants/MapStyles.ts';

interface MapComponentProps {
  stations: Station[];
  onStationClick: (station: Station) => void;
  center?: [number, number];
  zoom?: number;
  onFavoriteToggle?: (station: Station) => void;
  isFavorite?: (stationId: number) => boolean;
  onLocationUpdate?: (station: Station) => void;
  selectedStation?: Station | null;
}

interface GoogleMapProps {
  stations: Station[];
  onStationClick: (station: Station) => void;
  center: [number, number];
  zoom: number;
  onFavoriteToggle?: (station: Station) => void;
  isFavorite?: (stationId: number) => boolean;
  onLocationUpdate?: (station: Station) => void;
  selectedStation?: Station | null;
}

const GoogleMapComponent: React.FC<GoogleMapProps> = ({ stations, onStationClick, center, zoom, onFavoriteToggle, isFavorite, onLocationUpdate, selectedStation }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow>();
  const [mapReady, setMapReady] = useState(false);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const selectedMarkerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    if (ref.current && !map) {
      console.log('🚀 Initializing Google Maps...');
      try {
        const dynamicZoom = zoom > 14 ? 15 : 12;

        // Mobile-optimized map options for better performance
        const isMobile = window.innerWidth < 768;

        // If there's a selected station, center on it immediately to prevent flickering
        const initialCenter = selectedStation
          ? { lat: selectedStation.latitude, lng: selectedStation.longitude }
          : { lat: center[0], lng: center[1] };

        const mapOptions: google.maps.MapOptions = {
          center: initialCenter,
          zoom: dynamicZoom,
          mapTypeId: google.maps.MapTypeId.HYBRID,
          tilt: isMobile ? 0 : 45, // Disable tilt on mobile for performance
          heading: 0,
          draggable: true,
          scrollwheel: true,
          disableDoubleClickZoom: false,
          gestureHandling: 'greedy',
          // Mobile performance optimizations
          clickableIcons: false,
          disableDefaultUI: isMobile, // Hide all controls on mobile
          mapTypeControl: !isMobile,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: !isMobile,
          // Simplified styles for mobile performance
          styles: isMobile ? [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
            { featureType: "administrative", elementType: "labels", stylers: [{ visibility: "simplified" }] }
          ] : [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
          ]
        };

        // Try to apply custom map style
        try {
          mapOptions.mapId = CUSTOM_MAP_STYLE.mapId;
        } catch (error) {
          console.warn('⚠️ Could not apply custom map style:', error);
        }

        const newMap = new google.maps.Map(ref.current, mapOptions);
        setMap(newMap);

        const newInfoWindow = new google.maps.InfoWindow();
        setInfoWindow(newInfoWindow);

        // Wait for map to be fully loaded and tiles rendered before showing
        google.maps.event.addListenerOnce(newMap, 'tilesloaded', () => {
          console.log('🎯 Map tiles loaded - showing map');
          setMapReady(true);
        });

        // Fallback in case tilesloaded doesn't fire
        const fallbackTimeout = setTimeout(() => {
          console.log('⏰ Fallback - showing map after timeout');
          setMapReady(true);
        }, 2000);

        // Clean up fallback if tilesloaded fires first
        google.maps.event.addListenerOnce(newMap, 'tilesloaded', () => {
          clearTimeout(fallbackTimeout);
        });

        // CRITICAL: Force resize after brief delay
        setTimeout(() => {
          google.maps.event.trigger(newMap, 'resize');
          newMap.setCenter(initialCenter);
        }, 100);

        console.log('🗺️ Google Maps initialized successfully');
      } catch (error) {
        console.error('❌ Error initializing Google Maps:', error);
      }
    }
  }, [ref, map, center, zoom, selectedStation]);

  // Clear existing markers
  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  };

  // Optimized marker icons - Pre-generated for performance
  const createSimpleIcon = (status: string, isSelected: boolean = false): google.maps.Icon => {
    const color = status === 'available' ? '#00DD00' : '#ef4444';
    const size = window.innerWidth < 768 ? 24 : 32; // Smaller on mobile

    // For selected stations, create a pulsing effect with larger size and different styling
    if (isSelected) {
      const selectedSize = size * 1.5;
      const svgString = `<svg width="${selectedSize}" height="${selectedSize}" viewBox="0 0 ${selectedSize} ${selectedSize}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${selectedSize/2}" cy="${selectedSize/2}" r="${selectedSize/2-2}" fill="${color}" stroke="#FFD700" stroke-width="4" opacity="0.9"/>
        <circle cx="${selectedSize/2}" cy="${selectedSize/2}" r="${selectedSize/2-8}" fill="#FFD700" stroke="white" stroke-width="2" opacity="0.7"/>
        <text x="${selectedSize/2}" y="${selectedSize/2+4}" text-anchor="middle" fill="white" font-size="${selectedSize/4}" font-weight="bold">⛽</text>
      </svg>`;

      return {
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
        size: new google.maps.Size(selectedSize, selectedSize),
        anchor: new google.maps.Point(selectedSize/2, selectedSize/2),
        scaledSize: new google.maps.Size(selectedSize, selectedSize)
      };
    }

    // Simple, fast circle icon for mobile performance
    const svgString = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg"><circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="${color}" stroke="white" stroke-width="2"/><text x="${size/2}" y="${size/2+3}" text-anchor="middle" fill="white" font-size="${size/3}" font-weight="bold">⛽</text></svg>`;

    return {
      url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
      size: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size/2, size/2),
      scaledSize: new google.maps.Size(size, size)
    };
  };

  // Simplified info window content for better performance
  const createInfoWindowContent = useCallback((station: Station): string => {
    const status = station.latest_report?.status || 'unknown';
    const statusText = status === 'available' ? '🟢 Disponible' : '🔴 Rupture';
    const isStationFavorite = isFavorite ? isFavorite(station.id) : false;
    const favoriteIcon = isStationFavorite ? '❤️' : '🤍';
    const favoriteText = isStationFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris';

    // Votes/contestations info
    const confirmCount = station.latest_report?.confirm_count || 0;
    const contestCount = station.latest_report?.contest_count || 0;
    const totalVotes = confirmCount + contestCount;
    const hasVotes = totalVotes > 0;

    return `
      <div style="padding: 12px; min-width: 200px; max-width: 280px;">
        <div style="margin-bottom: 8px;">
          <h3 style="font-weight: 600; margin: 0; color: #1f2937;">${station.name}</h3>
          <p style="margin: 2px 0; color: #6b7280; font-size: 14px;">${station.brand}</p>
        </div>

        <div style="margin-bottom: 8px; color: #374151; font-size: 14px;">
          📍 ${station.address}<br>
          🏢 ${station.commune}
        </div>

        <div style="margin-bottom: 12px; padding: 8px; background: #f9fafb; border-radius: 6px;">
          <div style="font-weight: 500; font-size: 14px; margin-bottom: 4px;">${statusText}</div>
          ${station.latest_report?.price ? `<div style="color: #059669; font-size: 13px;">💰 ${station.latest_report.price} FCFA/L</div>` : ''}
          ${hasVotes ? `
            <div style="margin-top: 6px; font-size: 12px; color: #6b7280;">
              ${confirmCount > 0 ? `<span style="color: #059669;">✅ ${confirmCount} confirmations</span>` : ''}
              ${confirmCount > 0 && contestCount > 0 ? '<br>' : ''}
              ${contestCount > 0 ? `<span style="color: #dc2626;">❌ ${contestCount} contestations</span>` : ''}
            </div>
          ` : ''}
        </div>

        <div style="display: flex; gap: 8px;">
          ${onFavoriteToggle ? `
            <button onclick="window.handleFavoriteToggle(${station.id})" style="background: ${isStationFavorite ? '#dc2626' : '#6b7280'}; color: white; border: none; padding: 8px 12px; border-radius: 6px; cursor: pointer; font-size: 14px;" title="${favoriteText}">
              ${favoriteIcon}
            </button>
          ` : ''}
          ${onLocationUpdate ? `
            <button onclick="window.handleLocationUpdate(${station.id})" style="background: #10b981; color: white; border: none; padding: 8px 12px; border-radius: 6px; cursor: pointer; font-size: 14px;" title="Corriger la position">
              📍
            </button>
          ` : ''}
          <button onclick="window.handleStationClick(${station.id})" style="flex: 1; background: #2563eb; color: white; border: none; padding: 8px 12px; border-radius: 6px; cursor: pointer; font-size: 14px;">
            📝 Signaler
          </button>
        </div>
      </div>
    `;
  }, [isFavorite, onFavoriteToggle, onLocationUpdate]);

  // Optimized marker rendering with batching and performance improvements
  useEffect(() => {
    if (map && stations) {
      const startTime = performance.now();

      try {
        clearMarkers();

        // Global icon cache for better performance
        const iconCache = new Map<string, google.maps.Icon>();
        const getIcon = (status: string, isSelected: boolean = false): google.maps.Icon => {
          const key = `${status}-${window.innerWidth < 768 ? 'mobile' : 'desktop'}-${isSelected ? 'selected' : 'normal'}`;
          if (!iconCache.has(key)) {
            iconCache.set(key, createSimpleIcon(status, isSelected));
          }
          return iconCache.get(key)!;
        };

        // Add global click handler for info window buttons
        (window as any).handleStationClick = (stationId: number) => {
          const station = stations.find(s => s.id === stationId);
          if (station && onStationClick) {
            onStationClick(station);
            if (infoWindow) infoWindow.close();
          }
        };

        // Add global favorite toggle handler
        (window as any).handleFavoriteToggle = (stationId: number) => {
          const station = stations.find(s => s.id === stationId);
          if (station && onFavoriteToggle) {
            onFavoriteToggle(station);
            // Update the info window content to reflect the new favorite state
            if (infoWindow) {
              setTimeout(() => {
                infoWindow.setContent(createInfoWindowContent(station));
              }, 100);
            }
          }
        };

        // Add global location update handler
        (window as any).handleLocationUpdate = (stationId: number) => {
          const station = stations.find(s => s.id === stationId);
          if (station && onLocationUpdate) {
            onLocationUpdate(station);
            if (infoWindow) infoWindow.close();
          }
        };

        // Batch marker creation for better performance
        const markerPromises: Promise<google.maps.Marker>[] = [];

        // Show all stations on both mobile and desktop
        const stationsToShow = stations;

        stationsToShow.forEach((station, index) => {
          // Add small delay for smooth rendering on large datasets
          const createMarkerPromise = new Promise<google.maps.Marker>((resolve) => {
            const timeoutDelay = Math.floor(index / 20) * 2; // Slight delay for smooth rendering

            setTimeout(() => {
              try {
                const status = station.latest_report?.status || 'unknown';
                const isSelected = selectedStation && selectedStation.id === station.id;
                const icon = getIcon(status, isSelected);

                const marker = new google.maps.Marker({
                  position: { lat: station.latitude, lng: station.longitude },
                  map: map,
                  title: `${station.name} - ${status === 'available' ? 'Disponible' : 'Rupture'}`,
                  icon: icon,
                  optimized: true,
                  clickable: true
                });

                // Store reference to selected marker for potential highlighting
                if (isSelected) {
                  selectedMarkerRef.current = marker;

                  // Open info window for selected station (map should already be centered)
                  setTimeout(() => {
                    if (map && infoWindow) {
                      infoWindow.setContent(createInfoWindowContent(station));
                      infoWindow.open(map, marker);
                    }
                  }, 100); // Brief delay to ensure marker is ready
                }

                // Lazy load info windows on click for performance
                marker.addListener('click', () => {
                  if (infoWindow) {
                    infoWindow.setContent(createInfoWindowContent(station));
                    infoWindow.open(map, marker);
                  }
                });

                markersRef.current.push(marker);
                resolve(marker);
              } catch (error) {
                console.warn(`⚠️ Error creating marker for ${station.name}:`, error);
                resolve(null as any);
              }
            }, timeoutDelay);
          });

          markerPromises.push(createMarkerPromise);
        });

        // Log performance
        Promise.all(markerPromises).then(() => {
          const endTime = performance.now();
          console.log(`🚀 Added ${markersRef.current.length} markers in ${Math.round(endTime - startTime)}ms`);
        });

      } catch (error) {
        console.error('❌ Error adding markers to map:', error);
      }
    }
  }, [map, stations, infoWindow, onStationClick, selectedStation, onFavoriteToggle, onLocationUpdate, isFavorite, createInfoWindowContent]);

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ height: '100%', minHeight: '100%' }}>
      {/* Loading overlay */}
      {!mapReady && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Chargement de la carte...</p>
          </div>
        </div>
      )}

      {/* Map container - initially hidden */}
      <div
        ref={ref}
        className={`w-full h-full overflow-hidden transition-opacity duration-300 ${mapReady ? 'opacity-100' : 'opacity-0'}`}
        style={{ height: '100%', minHeight: '100%' }}
      />
    </div>
  );
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Chargement de Google Maps...</p>
          </div>
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="flex items-center justify-center h-full w-full bg-red-50 rounded-lg">
          <div className="text-center text-red-600 p-4">
            <p className="font-semibold">Erreur lors du chargement de Google Maps</p>
            <p className="text-sm mt-1">Vérifiez votre connexion internet</p>
          </div>
        </div>
      );
    case Status.SUCCESS:
      return null;
  }
};

const MapComponent: React.FC<MapComponentProps> = ({
  stations,
  onStationClick,
  center = [12.6392, -8.0029], // Centre de Bamako
  zoom = 12,
  onFavoriteToggle,
  isFavorite,
  onLocationUpdate,
  selectedStation
}) => {
  return (
    <div className="h-full w-full rounded-lg overflow-hidden">
      <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render}>
        <GoogleMapComponent
          stations={stations}
          onStationClick={onStationClick}
          center={center}
          zoom={zoom}
          onFavoriteToggle={onFavoriteToggle}
          isFavorite={isFavorite}
          onLocationUpdate={onLocationUpdate}
          selectedStation={selectedStation}
        />
      </Wrapper>
    </div>
  );
};

export default MapComponent;